/**
 * Σελίδα «Στατιστικά Πανελληνίων» (sosaepp): στατιστική ανάλυση του τι έχει
 * πέσει στις Πανελλαδικές και στις Επαναληπτικές — ανά κεφάλαιο/ενότητα του
 * βιβλίου, ανά έννοια και ανά θέμα. Δεδομένα: public/panelladikes.json (ίδια με
 * τη σελίδα «Πανελλήνιες»).
 */
import { examKey } from './panelladikes-exam';

interface Chapter {
  book: string;
  number: string;
  title: string;
}
interface Section {
  book: string;
  number: string;
  title: string;
  chapter: string;
}
interface Tag {
  id: number;
  label: string;
}
interface ExerciseType {
  id: string;
  label: string;
}
interface Exam {
  year: number;
  kind?: string;
}
interface BookSectionRef {
  book: string;
  section_number: string;
  relevance: string;
}
interface Exercise {
  exam_year: number;
  exam_kind?: string;
  theme: string;
  exercise_type: string;
  book_sections: BookSectionRef[];
  tag_ids: number[];
}
interface Data {
  chapters: Chapter[];
  sections: Section[];
  tags: Tag[];
  exercise_types: ExerciseType[];
  exams: Exam[];
  exercises: Exercise[];
}
interface Unit {
  key: string;
  year: number;
  repeat: boolean;
  label: string;
}

function byId<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (el === null) throw new Error('Λείπει το στοιχείο #' + id);
  return el as T;
}

let data: Data;
let units: Unit[] = [];
let typeLabel = new Map<string, string>();

const kindEl = byId<HTMLSelectElement>('sos-kind');
const themeEl = byId<HTMLSelectElement>('sos-theme');
const rootEl = byId<HTMLElement>('sos-root');

function h<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (className !== undefined) el.className = className;
  if (text !== undefined) el.textContent = text;
  return el;
}

const pct = (n: number, d: number): number => (d === 0 ? 0 : Math.round((n / d) * 100));

/** Οι εξετάσεις (έτος + είδος) που μετράνε στον παρονομαστή των ποσοστών. */
function activeUnits(): Unit[] {
  const kind = kindEl.value;
  return units.filter((u) => kind === 'all' || (kind === 'repeat') === u.repeat);
}

/** Οι ασκήσεις που περνούν από τα φίλτρα (είδος εξέτασης, θέμα). */
function activeExercises(): Exercise[] {
  const kind = kindEl.value;
  const theme = themeEl.value;
  return data.exercises.filter((e) => {
    if (kind !== 'all' && (kind === 'repeat') !== (e.exam_kind === 'repeat')) return false;
    return theme === 'all' || e.theme === theme;
  });
}

/** Πίνακας: σε πόσες εξετάσεις (και ποιες) εμφανίστηκε κάθε τύπος άσκησης. */
function renderTypeFrequency(exs: Exercise[], us: Unit[]): HTMLElement {
  const sec = h('section', 'sos-card');
  sec.append(h('h2', undefined, 'Συχνότητα εμφάνισης των τύπων ασκήσεων'), h('p', 'sos-sub', 'Σε πόσες από τις εξετάσεις εμφανίστηκε κάθε τύπος άσκησης (γεμάτο τετράγωνο = εμφανίστηκε εκείνη τη χρονιά).'));
  const perType = new Map<string, { presence: Set<string>; exercises: number; themes: Map<string, number> }>();
  exs.forEach((e) => {
    const rec = perType.get(e.exercise_type) ?? { presence: new Set<string>(), exercises: 0, themes: new Map<string, number>() };
    rec.presence.add(examKey(e));
    rec.exercises++;
    rec.themes.set(e.theme, (rec.themes.get(e.theme) ?? 0) + 1);
    perType.set(e.exercise_type, rec);
  });
  if (perType.size === 0) {
    sec.append(h('p', 'sos-empty', 'Δεν βρέθηκαν ασκήσεις με αυτά τα φίλτρα.'));
    return sec;
  }
  const rows = [...perType.entries()].sort((a, b) => b[1].presence.size - a[1].presence.size || b[1].exercises - a[1].exercises);
  const wrap = h('div', 'sos-table-wrap');
  const table = h('table', 'sos-table');
  const head = h('tr');
  head.append(h('th', undefined, 'Τύπος άσκησης'));
  us.forEach((u) => head.append(h('th', 'sos-col', u.label)));
  ['Εξετάσεις', '%', 'Ασκήσεις', 'Θέματα'].forEach((t) => head.append(h('th', undefined, t)));
  const thead = h('thead');
  thead.append(head);
  const tbody = h('tbody');
  rows.forEach(([type, rec]) => {
    const tr = h('tr');
    tr.append(h('td', 'sos-concept', typeLabel.get(type) ?? type));
    us.forEach((u) => {
      const on = rec.presence.has(u.key);
      const td = h('td', 'sos-cell' + (on ? ' sos-cell-on' : ''));
      td.title = (typeLabel.get(type) ?? type) + ' · ' + u.label + (on ? ' ✓' : ' —');
      tr.append(td);
    });
    const themes = [...rec.themes.entries()].sort((a, b) => b[1] - a[1]).map(([t, n]) => t + ' (' + n + ')').join(', ');
    tr.append(
      h('td', 'sos-num', rec.presence.size + '/' + us.length),
      h('td', 'sos-num', pct(rec.presence.size, us.length) + '%'),
      h('td', 'sos-num', String(rec.exercises)),
      h('td', 'sos-years', themes)
    );
    tbody.append(tr);
  });
  table.append(thead, tbody);
  wrap.append(table);
  sec.append(wrap);
  return sec;
}

let conceptSort = 'total';

/** «33%» → «σχεδόν 1 στις 3 εξεταστικές» (κλάσμα με παρονομαστή έως 6, όσο πιο κοντά γίνεται). */
function inWords(p: number): string {
  if (p >= 100) return 'σε όλες τις εξεταστικές';
  if (p <= 0) return 'σε καμία εξεταστική';
  let best = { a: 1, b: 2, diff: Infinity };
  for (let b = 2; b <= 6; b++) {
    for (let a = 1; a < b; a++) {
      const diff = Math.abs(p - (100 * a) / b);
      if (diff < best.diff - 1e-9) best = { a, b, diff };
    }
  }
  const prefix = best.diff < 0.001 ? '' : best.diff < 1 ? 'σχεδόν ' : 'περίπου ';
  return prefix + best.a + ' στις ' + best.b + ' εξεταστικές';
}

/** Πίνακας: σε ποιο ποσοστό των εξετάσεων εμφανίστηκε κάθε έννοια, ανά θέμα (Α–Δ) και συνολικά. */
function renderConceptsByTheme(exs: Exercise[], us: Unit[]): HTMLElement {
  const sec = h('section', 'sos-card');
  const themes = themeEl.value === 'all' ? ['Α', 'Β', 'Γ', 'Δ'] : [themeEl.value];
  const n = us.length;
  if (conceptSort !== 'total' && !themes.includes(conceptSort)) conceptSort = 'total';
  sec.append(h('h2', undefined, 'Ποσοστό εμφάνισης των εννοιών ανά θέμα'));
  const rows = data.tags.map((tag) => {
    const mine = exs.filter((e) => e.tag_ids.includes(tag.id));
    const count = (pred: (e: Exercise) => boolean): number => new Set(mine.filter(pred).map(examKey)).size;
    const per = themes.map((t) => count((e) => e.theme === t));
    return { label: tag.label, per, total: count(() => true), exercises: mine.length };
  });
  const col = themes.indexOf(conceptSort);
  const key = (r: { per: number[]; total: number }): number => (col >= 0 ? (r.per[col] ?? 0) : r.total);
  rows.sort((a, b) => key(b) - key(a) || b.total - a.total || b.exercises - a.exercises || a.label.localeCompare(b.label, 'el'));

  sec.append(
    h(
      'p',
      'sos-sub',
      'Για κάθε έννοια βλέπεις πόσο συχνά έχει πέσει στα θέματα ' + themes.join(', ') + '. Το «Σύνολο» δείχνει πόσο συχνά έχει πέσει συνολικά, σε οποιοδήποτε θέμα.'
    )
  );
  const first = rows[0];
  if (first !== undefined && first.total > 0) {
    const parts = themes.map((t, i) => {
      const p = pct(first.per[i] ?? 0, n);
      return p === 0 ? 'στο Θέμα ' + t + ' δεν εμφανίζεται καθόλου' : 'στο Θέμα ' + t + ' με ποσοστό ' + p + '%, δηλαδή ' + inWords(p);
    });
    const pt = pct(first.total, n);
    sec.append(
      h(
        'p',
        'sos-sub',
        'Παράδειγμα: η έννοια «' + first.label + '» εμφανίζεται ' + parts.join('· ') + '. Συνολικά, σε οποιοδήποτε θέμα, εμφανίζεται με ποσοστό ' + pt + '%, δηλαδή ' + inWords(pt) + '.'
      )
    );
  }
  if (exs.length === 0) {
    sec.append(h('p', 'sos-empty', 'Δεν βρέθηκαν ασκήσεις με αυτά τα φίλτρα.'));
    return sec;
  }

  const label = h('label', 'sos-control sos-sort', 'Ταξινόμηση κατά');
  const select = h('select');
  [['total', 'Σύνολο']].concat(themes.map((t) => [t, 'Θέμα ' + t])).forEach(([v, t]) => {
    const o = h('option', undefined, t);
    o.value = v ?? '';
    o.selected = v === conceptSort;
    select.append(o);
  });
  select.addEventListener('change', () => {
    conceptSort = select.value;
    render();
  });
  label.append(select);
  sec.append(label);

  const wrap = h('div', 'sos-table-wrap');
  const table = h('table', 'sos-table');
  const head = h('tr');
  head.append(h('th', undefined, 'Έννοια'));
  themes.forEach((t) => head.append(h('th', 'sos-col', 'Θέμα ' + t)));
  head.append(h('th', 'sos-col', 'Σύνολο'));
  const thead = h('thead');
  thead.append(head);
  const tbody = h('tbody');
  const cell = (k: number, strong: boolean): HTMLElement => {
    const p = pct(k, n);
    const td = h('td', 'sos-pct' + (strong ? ' sos-pct-total' : ''), p === 0 ? '–' : p + '%');
    td.style.background = 'color-mix(in srgb, var(--primary-blue) ' + Math.round(p * 0.6) + '%, transparent)';
    return td;
  };
  rows.forEach((r) => {
    const tr = h('tr');
    tr.append(h('td', 'sos-concept', r.label));
    r.per.forEach((k) => tr.append(cell(k, false)));
    tr.append(cell(r.total, true));
    tbody.append(tr);
  });
  table.append(thead, tbody);
  wrap.append(table);
  sec.append(wrap);
  return sec;
}

let sectionSort = 'total';

/** Σύντομα ονόματα των βιβλίων για τον πίνακα ενοτήτων. */
const BOOK_NAME: Record<string, string> = { main: 'Ανάπτυξη Εφαρμογών', supp: 'Πληροφορική (συμπλ.)' };

const sectionOrder = (num: string): number[] => num.split('.').map(Number);

/** Ενότητες που υπάρχουν και στα δύο βιβλία (πίνακας αντιστοίχισης του Βιβλίου 2) και ισχύουν και οι δύο στην ύλη: μετράνε σε μία γραμμή. */
const SHARED: { name: string; main: string; supp: string[] }[] = [
  { name: 'Στοίβα', main: '3.4', supp: ['1.1', '1.1.1', '1.1.2'] },
  { name: 'Ουρά', main: '3.5', supp: ['1.2', '1.2.1', '1.2.2'] },
  { name: 'Εντολή ΕΠΙΛΕΞΕ', main: '8.1.2', supp: ['3.1', '3.1.1', '3.1.2'] },
  { name: 'Κατηγορίες λαθών', main: '13.1', supp: ['5.1', '5.1.1', '5.1.2', '5.1.3'] },
  { name: 'Εκσφαλμάτωση', main: '13.2', supp: ['5.2', '5.2.1', '5.2.3', '5.2.4'] },
];
BOOK_NAME['both'] = 'Και τα δύο βιβλία';

interface SectionGroup {
  key: string;
  label: string;
  /** Βιβλίο εμφάνισης: main, supp ή both. */
  book: string;
  /** Κλειδί ταξινόμησης «σειράς βιβλίου». */
  order: string;
}

function sectionGroup(book: string, num: string, titles: Map<string, string>): SectionGroup {
  const shared = SHARED.find((g) => (book === 'main' ? g.main === num : book === 'supp' && g.supp.includes(num)));
  const pad = (n: string): string => n.split('.').map((x) => x.padStart(3, '0')).join('.');
  if (shared !== undefined) {
    return { key: 'both:' + shared.main, label: shared.main + ' · ' + (shared.supp[0] ?? '') + ' ' + shared.name, book: 'both', order: '0' + pad(shared.main) };
  }
  return { key: book + ':' + num, label: num + ' ' + (titles.get(book + ':' + num) ?? ''), book, order: (book === 'main' ? '0' : '1') + pad(num) };
}

const SHARED_NOTE = 'Στις γραμμές «Και τα δύο βιβλία» ο πρώτος αριθμός είναι της ενότητας στο «Ανάπτυξη Εφαρμογών» και ο δεύτερος στην «Πληροφορική (συμπλ.)».';

/** Πίνακας: σε ποιο ποσοστό των εξετάσεων εμφανίστηκε κάθε ενότητα των βιβλίων στα θέματα Α και Β. Τα Γ και Δ δεν μετράνε: κάθε ένα περνά από πολλές ενότητες μαζί. */
function renderSectionsByTheme(us: Unit[]): HTMLElement {
  const sec = h('section', 'sos-card');
  sec.append(h('h2', undefined, 'Ποσοστό εμφάνισης των ενοτήτων του βιβλίου'));
  const kind = kindEl.value;
  const wanted = themeEl.value;
  const themes = wanted === 'all' ? ['Α', 'Β'] : [wanted];
  if (themes.some((t) => t !== 'Α' && t !== 'Β')) {
    sec.append(h('p', 'sos-empty', 'Ο πίνακας αφορά μόνο τα θέματα Α και Β. Τα θέματα Γ και Δ περνούν από πολλές ενότητες μαζί και δεν μετράνε εδώ.'));
    return sec;
  }
  const n = us.length;
  if (sectionSort !== 'total' && !themes.includes(sectionSort)) sectionSort = 'total';
  const exs = data.exercises.filter((e) => (e.theme === 'Α' || e.theme === 'Β') && (kind === 'all' || (kind === 'repeat') === (e.exam_kind === 'repeat')));
  const titles = new Map(data.sections.map((x) => [x.book + ':' + x.number, x.title]));
  const groups = new Map<string, { g: SectionGroup; mine: Set<Exercise> }>();
  exs.forEach((e) =>
    e.book_sections.forEach((r) => {
      if (r.relevance !== 'primary') return;
      const g = sectionGroup(r.book, r.section_number, titles);
      const rec = groups.get(g.key) ?? { g, mine: new Set<Exercise>() };
      rec.mine.add(e);
      groups.set(g.key, rec);
    })
  );
  const rows = [...groups.values()].map(({ g, mine }) => {
    const list = [...mine];
    const count = (pred: (e: Exercise) => boolean): number => new Set(list.filter(pred).map(examKey)).size;
    return { book: g.book, order: g.order, label: g.label, per: themes.map((t) => count((e) => e.theme === t)), total: count(() => true), exercises: list.length };
  });
  const col = themes.indexOf(sectionSort);
  const key = (r: { per: number[]; total: number }): number => (col >= 0 ? (r.per[col] ?? 0) : r.total);
  rows.sort((a, b) => key(b) - key(a) || b.total - a.total || b.exercises - a.exercises || a.order.localeCompare(b.order));

  sec.append(
    h(
      'p',
      'sos-sub',
      'Για κάθε ενότητα των βιβλίων βλέπεις πόσο συχνά έχει πέσει στα θέματα ' + themes.join(' και ') + '. Το «Σύνολο» δείχνει πόσο συχνά έχει πέσει συνολικά, σε οποιοδήποτε από αυτά. Τα θέματα Γ και Δ δεν υπάρχουν εδώ, γιατί κάθε ένα περνά από πολλές ενότητες μαζί.'
    )
  );
  const first = rows[0];
  if (first !== undefined && first.total > 0) {
    const parts = themes.map((t, i) => {
      const p = pct(first.per[i] ?? 0, n);
      return p === 0 ? 'στο Θέμα ' + t + ' δεν εμφανίζεται καθόλου' : 'στο Θέμα ' + t + ' με ποσοστό ' + p + '%, δηλαδή ' + inWords(p);
    });
    const pt = pct(first.total, n);
    sec.append(
      h(
        'p',
        'sos-sub',
        'Παράδειγμα: η ενότητα «' + first.label + '» (' + (BOOK_NAME[first.book] ?? first.book) + ') εμφανίζεται ' + parts.join('· ') + '. Συνολικά εμφανίζεται με ποσοστό ' + pt + '%, δηλαδή ' + inWords(pt) + '.'
      )
    );
  }
  if (rows.length === 0) {
    sec.append(h('p', 'sos-empty', 'Δεν βρέθηκαν ασκήσεις με αυτά τα φίλτρα.'));
    return sec;
  }

  const label = h('label', 'sos-control sos-sort', 'Ταξινόμηση κατά');
  const select = h('select');
  [['total', 'Σύνολο']].concat(themes.map((t) => [t, 'Θέμα ' + t])).forEach(([v, t]) => {
    const o = h('option', undefined, t);
    o.value = v ?? '';
    o.selected = v === sectionSort;
    select.append(o);
  });
  select.addEventListener('change', () => {
    sectionSort = select.value;
    render();
  });
  label.append(select);
  sec.append(label);

  const wrap = h('div', 'sos-table-wrap');
  const table = h('table', 'sos-table');
  const head = h('tr');
  head.append(h('th', undefined, 'Βιβλίο'), h('th', undefined, 'Ενότητα'));
  themes.forEach((t) => head.append(h('th', 'sos-col', 'Θέμα ' + t)));
  head.append(h('th', 'sos-col', 'Σύνολο'));
  const thead = h('thead');
  thead.append(head);
  const tbody = h('tbody');
  const cell = (k: number, strong: boolean): HTMLElement => {
    const p = pct(k, n);
    const td = h('td', 'sos-pct' + (strong ? ' sos-pct-total' : ''), p === 0 ? '–' : p + '%');
    td.style.background = 'color-mix(in srgb, var(--primary-blue) ' + Math.round(p * 0.6) + '%, transparent)';
    return td;
  };
  rows.forEach((r) => {
    const tr = h('tr');
    tr.append(h('td', 'sos-book', BOOK_NAME[r.book] ?? r.book), h('td', 'sos-concept', r.label));
    r.per.forEach((k) => tr.append(cell(k, false)));
    tr.append(cell(r.total, true));
    tbody.append(tr);
  });
  table.append(thead, tbody);
  wrap.append(table);
  sec.append(wrap);
  const books = h('p', 'sos-note');
  books.textContent = 'Ανάπτυξη Εφαρμογών: «Ανάπτυξη Εφαρμογών σε Προγραμματιστικό Περιβάλλον» (Βιβλίο Μαθητή, Γ΄ Γενικού Λυκείου). Πληροφορική (συμπλ.): «Πληροφορική, Γ΄ Γενικού Λυκείου» (συμπληρωματικό εκπαιδευτικό υλικό). ' + SHARED_NOTE;
  sec.append(books);
  return sec;
}

const yearSort = new Map<string, string>();

interface YearRow {
  label: string;
  book?: string;
  order: string;
  present: Set<string>;
}

/** Πίνακας με τετράγωνα ανά εξέταση: ποιες χρονιές έπεσε κάθε γραμμή (έννοια ή ενότητα), πότε έπεσε τελευταία φορά και πόσες χρονιές έχουν περάσει από τότε. */
function renderYearGrid(id: string, title: string, sub: string, firstCol: string, orderLabel: string, rows: YearRow[], us: Unit[]): HTMLElement {
  const sec = h('section', 'sos-card');
  sec.append(h('h2', undefined, title), h('p', 'sos-sub', sub));
  const latest = Math.max(...us.map((u) => u.year));
  const data2 = rows.map((r) => {
    const years = us.filter((u) => r.present.has(u.key)).map((u) => u.year);
    const last = years.length > 0 ? Math.max(...years) : 0;
    return { ...r, count: r.present.size, last, gap: last > 0 ? latest - last : Infinity };
  });
  const sorters: Record<string, (a: (typeof data2)[number], b: (typeof data2)[number]) => number> = {
    total: (a, b) => b.count - a.count || b.last - a.last,
    recent: (a, b) => b.last - a.last || b.count - a.count,
    gap: (a, b) => (b.last > 0 ? b.gap : -1) - (a.last > 0 ? a.gap : -1) || a.count - b.count,
    order: (a, b) => a.order.localeCompare(b.order, 'el', { numeric: true }),
  };
  const mode = yearSort.get(id) ?? 'total';
  data2.sort((a, b) => (sorters[mode] ?? sorters['total']!)(a, b) || a.label.localeCompare(b.label, 'el'));
  const first = data2.find((r) => r.last > 0);
  if (first !== undefined) {
    const gapText = first.gap === 0 ? 'δηλαδή στην πιο πρόσφατη χρονιά' : 'δηλαδή έχουν περάσει ' + (first.gap === 1 ? 'μία χρονιά' : first.gap + ' χρονιές') + ' χωρίς να πέσει';
    sec.append(h('p', 'sos-sub', 'Παράδειγμα: «' + first.label + '» έπεσε τελευταία φορά το ' + first.last + ', ' + gapText + '.'));
  }
  const label = h('label', 'sos-control sos-sort', 'Ταξινόμηση κατά');
  const select = h('select');
  [['total', 'Σύνολο εξετάσεων'], ['recent', 'Πιο πρόσφατα'], ['gap', 'Περισσότερο καιρό χωρίς εμφάνιση'], ['order', orderLabel]].forEach(([v, t]) => {
    const o = h('option', undefined, t);
    o.value = v ?? '';
    o.selected = v === mode;
    select.append(o);
  });
  select.addEventListener('change', () => {
    yearSort.set(id, select.value);
    render();
  });
  label.append(select);
  sec.append(label);

  const wrap = h('div', 'sos-table-wrap');
  const table = h('table', 'sos-table sos-grid');
  const head = h('tr');
  if (data2.some((r) => r.book !== undefined)) head.append(h('th', undefined, 'Βιβλίο'));
  head.append(h('th', undefined, firstCol));
  us.forEach((u) => head.append(h('th', 'sos-col', u.label)));
  ['Εξετάσεις', 'Τελευταία φορά', 'Χρονιές χωρίς'].forEach((t) => head.append(h('th', undefined, t)));
  const thead = h('thead');
  thead.append(head);
  const tbody = h('tbody');
  data2.forEach((r) => {
    const tr = h('tr');
    if (r.book !== undefined) tr.append(h('td', 'sos-book', BOOK_NAME[r.book] ?? r.book));
    tr.append(h('td', 'sos-concept', r.label));
    us.forEach((u) => {
      const on = r.present.has(u.key);
      const td = h('td', 'sos-cell' + (on ? ' sos-cell-on' : ''));
      td.title = r.label + ' · ' + u.label + (on ? ' ✓' : ' —');
      tr.append(td);
    });
    tr.append(h('td', 'sos-num', r.count + '/' + us.length), h('td', 'sos-num', r.last > 0 ? String(r.last) : '—'), h('td', 'sos-num', r.last > 0 ? String(r.gap) : '—'));
    tbody.append(tr);
  });
  table.append(thead, tbody);
  wrap.append(table);
  sec.append(wrap);
  if (data2.some((r) => r.book === 'both')) sec.append(h('p', 'sos-note', SHARED_NOTE));
  return sec;
}

function conceptYearGrid(exs: Exercise[], us: Unit[]): HTMLElement {
  const rows = data.tags.map((t, i) => ({
    label: t.label,
    order: String(i).padStart(3, '0') + t.label,
    present: new Set(exs.filter((e) => e.tag_ids.includes(t.id)).map(examKey)),
  }));
  rows.sort((a, b) => a.label.localeCompare(b.label, 'el'));
  rows.forEach((r, i) => (r.order = String(i).padStart(3, '0')));
  return renderYearGrid('concepts', 'Πότε έπεσε κάθε έννοια', 'Για κάθε έννοια βλέπεις σε ποιες εξετάσεις έχει πέσει (γεμάτο τετράγωνο), πότε έπεσε τελευταία φορά και πόσες χρονιές έχουν περάσει από τότε.', 'Έννοια', 'Αλφαβητικά', rows, us);
}

function sectionYearGrid(us: Unit[]): HTMLElement {
  const kind = kindEl.value;
  const wanted = themeEl.value;
  const themes = wanted === 'all' ? ['Α', 'Β'] : [wanted];
  if (themes.some((t) => t !== 'Α' && t !== 'Β')) {
    const sec = h('section', 'sos-card');
    sec.append(h('h2', undefined, 'Πότε έπεσε κάθε ενότητα του βιβλίου'), h('p', 'sos-empty', 'Ο πίνακας αφορά μόνο τα θέματα Α και Β. Τα θέματα Γ και Δ περνούν από πολλές ενότητες μαζί και δεν μετράνε εδώ.'));
    return sec;
  }
  const titles = new Map(data.sections.map((x) => [x.book + ':' + x.number, x.title]));
  const groups = new Map<string, { g: SectionGroup; present: Set<string> }>();
  data.exercises
    .filter((e) => themes.includes(e.theme) && (kind === 'all' || (kind === 'repeat') === (e.exam_kind === 'repeat')))
    .forEach((e) =>
      e.book_sections.forEach((r) => {
        if (r.relevance !== 'primary') return;
        const g = sectionGroup(r.book, r.section_number, titles);
        const rec = groups.get(g.key) ?? { g, present: new Set<string>() };
        rec.present.add(examKey(e));
        groups.set(g.key, rec);
      })
    );
  const rows = [...groups.values()].map(({ g, present }) => ({ label: g.label, book: g.book, order: g.order, present }));
  return renderYearGrid('sections', 'Πότε έπεσε κάθε ενότητα του βιβλίου', 'Για κάθε ενότητα βλέπεις σε ποιες εξετάσεις έχει πέσει στα θέματα ' + themes.join(' και ') + ' (γεμάτο τετράγωνο), πότε έπεσε τελευταία φορά και πόσες χρονιές έχουν περάσει από τότε.', 'Ενότητα', 'Σειρά βιβλίου', rows, us);
}

/** Σημείωση στο τέλος: τα στατιστικά βασίζονται σε δική μας κατάταξη και δεν προβλέπουν τα θέματα. */
function renderDisclaimer(): HTMLElement {
  const sec = h('section', 'sos-card');
  sec.append(
    h('h2', undefined, 'Πριν βασιστείς στα στατιστικά'),
    h(
      'p',
      'sos-sub',
      'Τα στοιχεία βγήκαν από τα θέματα των Πανελλαδικών και των Επαναληπτικών εξετάσεων των ετών 2020 έως 2026. Τον τύπο κάθε άσκησης, τις έννοιες και τις ενότητες του βιβλίου τα καταχωρίσαμε εμείς, και μπορεί να υπάρχουν λάθη ή διαφορετικές αντιλήψεις σε κάποιες οριακές περιπτώσεις. Τα θέματα Γ και Δ περνούν από πολλές ενότητες μαζί, γι\' αυτό δεν μετράνε στους πίνακες των ενοτήτων. Τα ποσοστά δείχνουν τι έχει πέσει μέχρι τώρα και δεν προβλέπουν τι θα πέσει φέτος: χρησιμοποίησέ τα ως οδηγό για την επανάληψη και όχι ως υποκατάστατο της ύλης.'
    )
  );
  return sec;
}

function render(): void {
  const exs = activeExercises();
  const us = activeUnits();
  rootEl.replaceChildren(renderTypeFrequency(exs, us), renderConceptsByTheme(exs, us), conceptYearGrid(exs, us), renderSectionsByTheme(us), sectionYearGrid(us), renderDisclaimer());
}

async function init(): Promise<void> {
  try {
    const res = await fetch('./panelladikes.json', { cache: 'no-cache' });
    if (!res.ok) throw new Error(String(res.status));
    data = (await res.json()) as Data;
  } catch {
    const msg = document.createElement('p');
    msg.className = 'sos-empty';
    msg.textContent = 'Δεν ήταν δυνατή η φόρτωση των δεδομένων. Δοκίμασε ξανά.';
    rootEl.replaceChildren(msg);
    return;
  }
  typeLabel = new Map(data.exercise_types.map((t) => [t.id, t.label]));
  units = data.exams
    .map((e) => {
      const repeat = e.kind === 'repeat';
      return { key: examKey({ exam_year: e.year, exam_kind: e.kind }), year: e.year, repeat, label: e.year + (repeat ? ' Επ.' : '') };
    })
    .sort((a, b) => a.year - b.year || Number(a.repeat) - Number(b.repeat));
  [kindEl, themeEl].forEach((el) => el.addEventListener('change', render));
  render();
}

void init();
